import { type ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  Fields,
  asList,
  asRecord,
  erased,
  hasValue,
  type V2Record,
} from "./AllMattersV2Fields";
import {
  DataTable,
  KeyValues,
  PartyTable,
  Section,
  Tag,
  area,
  formatDate,
  locations,
  text,
} from "./AllMattersV2Parts";

const current = (record: V2Record, key: string): unknown =>
  `現在の${key}` in record ? record[`現在の${key}`] : record[key];

/** Finds the 表題部現在値の判定 of fields under one part, such as `/表題部/土地`. */
type Judge = (...fields: string[]) => V2Record | undefined;

const judges =
  (judgements: unknown) =>
  (base: string): Judge =>
  (...fields) => {
    const found = asList(judgements)
      .map(asRecord)
      .filter((judgement) =>
        fields.some((field) => judgement.対象 === `${base}/${field}`)
      );
    return found.find(({ 状態 }) => 状態 === "未確定") ?? found[0];
  };

/**
 * A current value with its judgement. 未確定 values are never hidden: an empty
 * one reads 未確定 with its reasons, a computed one keeps the reasons below.
 */
const judged = (value: ReactNode, judgement?: V2Record): ReactNode => {
  if (judgement?.状態 !== "未確定") return value;
  const reasons = asList(judgement.未解決).map(text).filter(Boolean);
  const note = reasons.length ? `（${reasons.join("、")}）` : "";
  return value == null || value === "" ? (
    <>
      <Box component="span" fontWeight={700}>
        未確定
      </Box>
      {note}
    </>
  ) : (
    <>
      {value}
      <Typography variant="caption" color="text.secondary" component="div">
        未確定{note}
      </Typography>
    </>
  );
};

/** Floor areas as separate lines: `1階 60 ㎡`. */
const Floors = ({
  floors,
  showErased,
}: {
  floors: unknown;
  showErased: boolean;
}): JSX.Element | null => {
  if (!Array.isArray(floors))
    return floors == null ? null : <>{area(floors)}</>;
  const visible = floors.filter((floor) => showErased || !erased(floor));
  if (!visible.length) return null;
  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", flexWrap: "wrap", columnGap: 2 }}
    >
      {visible.map((floor, index) => {
        const item = asRecord(floor);
        return (
          <Box
            key={index}
            component="span"
            sx={{
              whiteSpace: "nowrap",
              ...(erased(item)
                ? { color: "text.secondary", textDecoration: "underline" }
                : {}),
            }}
          >
            {text(item.階)} <span>{area(item.床面積)}</span>
          </Box>
        );
      })}
    </Box>
  );
};

/** 原因 chips, the cause date and the source wording, as in the PDF's 原因欄. */
const Cause = ({ cause }: { cause: unknown }): JSX.Element | null => {
  const item = asRecord(cause);
  const source = typeof cause === "string" ? cause : text(item.原文);
  const kinds = asList(item.区分).map(String);
  const date = formatDate(item.原因日付);
  const registered = formatDate(item.登記日付);
  if (!source && !kinds.length) return null;
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
        {kinds.map((kind) => (
          <Tag key={kind} label={kind} tone="primary" />
        ))}
        {item.職権 === true && <Tag label="職権" />}
        {(date || registered) && (
          <Typography variant="caption" color="text.secondary">
            {[date && `原因日 ${date}`, registered && `登記 ${registered}`]
              .filter(Boolean)
              .join(" / ")}
          </Typography>
        )}
      </Stack>
      {source && (
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {source}
        </Typography>
      )}
    </Stack>
  );
};

const LandTitle = ({
  land,
  judge,
}: {
  land: V2Record;
  judge: Judge;
}): JSX.Element => {
  const details = asList(land.明細);
  const places = asList(land.所在);
  return (
    <>
      <Section title="現在の土地の表示" tone="success">
        <KeyValues
          items={[
            [
              "所在",
              judged(locations(current(land, "所在")), judge("現在の所在")),
            ],
            ["地番", judged(text(current(land, "地番")), judge("現在の地番"))],
            ["地目", judged(text(current(land, "地目")), judge("現在の地目"))],
            ["地積", judged(area(current(land, "地積")), judge("現在の地積"))],
            [
              "地図番号",
              judged(text(current(land, "地図番号")), judge("現在の地図番号")),
            ],
          ]}
        />
        {hasValue(land.現在の地目地積) && (
          <Fields value={land.現在の地目地積} showErased />
        )}
      </Section>
      {details.length > 0 && (
        <Section title="地番・地目・地積の変遷" tone="success">
          <DataTable
            columns={[
              { label: "地番", width: "6em" },
              { label: "地目", width: "4.5em" },
              { label: "地積", width: "6.5em" },
              { label: "原因" },
            ]}
            rows={details.map((detail, index) => {
              const item = asRecord(detail);
              return {
                key: index,
                cells: [
                  text(item.地番),
                  text(item.地目),
                  area(item.地積),
                  <Cause key="cause" cause={item.原因} />,
                ],
              };
            })}
          />
        </Section>
      )}
      {places.length > 1 && (
        <Section title="所在の変遷" tone="success">
          <DataTable
            columns={[{ label: "所在" }, { label: "変更", width: "40%" }]}
            rows={places.map((place, index) => {
              const item = asRecord(place);
              return {
                key: index,
                cells: [
                  text(item.所在 ?? item.原文),
                  [text(item.変更原因), formatDate(item.変更日)]
                    .filter(Boolean)
                    .join(" "),
                ],
              };
            })}
          />
        </Section>
      )}
    </>
  );
};

const buildingItems = (
  building: V2Record,
  showErased: boolean,
  judge: Judge
): Array<[string, ReactNode]> => [
  // Schema 6.2 gives the building's current location as one display line.
  [
    "所在",
    judged(
      text(building.現在の所在表示) || locations(current(building, "所在")),
      judge("現在の所在")
    ),
  ],
  [
    "家屋番号",
    judged(text(current(building, "家屋番号")), judge("現在の家屋番号")),
  ],
  [
    "建物の名称",
    judged(text(current(building, "建物の名称")), judge("現在の建物の名称")),
  ],
  ["種類", judged(text(current(building, "種類")), judge("現在の種類"))],
  ["構造", judged(text(current(building, "構造")), judge("現在の構造"))],
  [
    "床面積",
    judged(
      hasValue(current(building, "各階床面積") ?? building.現在の床面積) ? (
        <Floors
          floors={
            current(building, "各階床面積") ?? current(building, "床面積")
          }
          showErased={showErased}
        />
      ) : null,
      judge("現在の各階床面積", "現在の床面積")
    ),
  ],
  [
    "延床面積",
    judged(
      area(building.延床面積 ?? building.床面積合計),
      judge("延床面積", "床面積合計")
    ),
  ],
  ["新築年月日", judged(text(building.新築年月日), judge("新築年月日"))],
];

const StateHistory = ({
  title,
  states,
  showErased,
}: {
  title: string;
  states: unknown[];
  showErased: boolean;
}): JSX.Element | null =>
  states.length ? (
    <Section title={title} tone="warning">
      <DataTable
        columns={[
          { label: "種類", width: "5em" },
          { label: "構造" },
          { label: "床面積", width: "9em" },
          { label: "原因" },
        ]}
        rows={states.map((state, index) => {
          const item = asRecord(state);
          return {
            key: index,
            cells: [
              text(item.種類),
              text(item.構造),
              <Floors
                key="floors"
                floors={item.各階床面積}
                showErased={showErased}
              />,
              <Cause key="cause" cause={item.原因} />,
            ],
          };
        })}
      />
    </Section>
  ) : null;

const BuildingTitle = ({
  building,
  showErased,
  judge,
}: {
  building: V2Record;
  showErased: boolean;
  judge: Judge;
}): JSX.Element => (
  <>
    <Section title="現在の建物の表示" tone="warning">
      <KeyValues items={buildingItems(building, showErased, judge)} />
    </Section>
    <StateHistory
      title="主たる建物の変遷"
      states={asList(building.主たる建物)}
      showErased={showErased}
    />
    {hasValue(building.附属建物) && (
      <Section title="附属建物" tone="warning">
        <Fields value={building.附属建物} showErased={showErased} />
      </Section>
    )}
  </>
);

const CondoTitle = ({
  condo,
  showErased,
  judge,
}: {
  condo: V2Record;
  showErased: boolean;
  judge: (part: string) => Judge;
}): JSX.Element => {
  const whole = asRecord(condo.一棟の建物);
  const unit = asRecord(condo.専有部分);
  const rights = asList(condo.敷地権).filter(
    (right) => showErased || !erased(right)
  );
  return (
    <>
      {hasValue(condo.一棟の建物) && (
        <Section title="一棟の建物の表示" tone="info">
          <KeyValues
            items={buildingItems(
              whole,
              showErased,
              judge("/一棟の建物")
            ).filter(
              ([label]) => !["家屋番号", "種類", "新築年月日"].includes(label)
            )}
          />
        </Section>
      )}
      {hasValue(condo.敷地権の目的である土地) && (
        <Section title="敷地権の目的である土地" tone="info">
          <Fields
            value={condo.敷地権の目的である土地}
            showErased={showErased}
          />
        </Section>
      )}
      {hasValue(condo.専有部分) && (
        <Section title="専有部分の建物の表示" tone="info">
          <KeyValues
            items={buildingItems(unit, showErased, judge("/専有部分"))}
          />
        </Section>
      )}
      <StateHistory
        title="専有部分の変遷"
        states={asList(unit.状態)}
        showErased={showErased}
      />
      {rights.length > 0 && (
        <Section title="敷地権の表示" tone="info">
          <DataTable
            columns={[
              { label: "符号", width: "4em" },
              { label: "敷地権の種類" },
              { label: "敷地権の割合" },
            ]}
            rows={rights.map((right, index) => {
              const item = asRecord(right);
              return {
                key: index,
                erased: erased(item),
                cells: [
                  text(item.符号),
                  text(item.種類 ?? item.敷地権の種類),
                  text(item.割合 ?? item.敷地権の割合),
                ],
              };
            })}
          />
        </Section>
      )}
      {hasValue(condo.附属建物) && (
        <Section title="附属建物" tone="info">
          <Fields value={condo.附属建物} showErased={showErased} />
        </Section>
      )}
    </>
  );
};

export const AllMattersV2Title = ({
  value,
  judgements,
  showErased,
}: {
  value: unknown;
  /** ビュー.表題部現在値の判定 (Schema 5.3 and later). */
  judgements?: unknown;
  showErased: boolean;
}): JSX.Element => {
  const title = asRecord(value);
  const judge = judges(judgements);
  return (
    <Stack spacing={2.5}>
      {hasValue(title.土地) && (
        <LandTitle land={asRecord(title.土地)} judge={judge("/表題部/土地")} />
      )}
      {hasValue(title.建物) && (
        <BuildingTitle
          building={asRecord(title.建物)}
          showErased={showErased}
          judge={judge("/表題部/建物")}
        />
      )}
      {hasValue(title.区分建物) && (
        <CondoTitle
          condo={asRecord(title.区分建物)}
          showErased={showErased}
          judge={(part) => judge(`/表題部/区分建物${part}`)}
        />
      )}
      {[title.土地, title.建物, title.区分建物].map((part, index) => {
        const owners = asList(asRecord(part).所有者).filter(
          (owner) => showErased || !erased(owner)
        );
        return owners.length ? (
          <Section key={index} title="表題部の所有者">
            <PartyTable parties={owners} />
          </Section>
        ) : null;
      })}
    </Stack>
  );
};
